import React, { useState, useEffect } from 'react'
import glamorous from 'glamorous'
import {
  HeadingText,
  v2grey600,
  grey600,
  grey700,
  blue600,
  v2black,
  Button,
  blue100,
  v2blue400,
  v2blue500,
  V2MerchantIcon,
  Divider,
  white,
  v2neutral100,
  V2ShareInstagramIcon,
  V2ShareTwitterIcon,
  V2ShareFacebookIcon,
  V2WebsiteIcon,
  V2ShareIcon,
  V2MailIcon,
  V2PhoneIcon,
  Link,
} from '@paypalcorp/pp-react'
import getContent from 'pp-react-l10n'
const l10n = getContent('pages/seller/preview')

const Container = glamorous.div({
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  margin: '-30px 0 0 0',
  '& div[role=heading]': {
    color: grey700,
    textAlign: 'left',
  },
  '& div[role=heading] + p': {
    color: grey600,
    fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
    fontSize: '1rem',
    margin: '0.5rem 0',
  },
})

const BackgroundPhoto = glamorous.div(({ image }) => ({
  boxSizing: 'border-box',
  width: '100%',
  '& > div:first-child': {
    borderRadius: '13px 13px 0 0',
    backgroundImage: `url(${encodeURI(image)})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: '100%',
    paddingBottom: '32%',
    maxHeight: 293,
    position: 'relative',
  },
}))

const GenericAvatar = glamorous.div(({ background }) => ({
  background,
  borderRadius: '50%',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  ' span': {
    color: v2blue500,
  },
}))

const Logo = glamorous.div(({ image }) => ({
  height: '20vw',
  width: '20vw',
  maxHeight: 85,
  maxWidth: 85,
  borderRadius: '50%',
  backgroundImage: `url(${encodeURI(image)})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundColor: v2neutral100,
  position: 'absolute',
  left: 'calc(5% + 10px)',
  bottom: 0,
  transform: 'translateY(50%)',
  border: white + ' solid 3px',
  boxSizing: 'content-box',
}))

const ShadowBlock = glamorous.div({
  borderRadius: 13,
  minHeight: 400,
  margin: '40px 0',
  backgroundColor: white,
  boxShadow: '0 0 6px rgba(0, 0, 0, 0.4)',
  '& p': {
    fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  },
})

const disabledMod = {
  '& .disabledLink, & .disabledLink:focus, & .disabledLink:active, & .disabledLink:hover': {
    pointerEvents: 'none',
    textDecoration: 'none',
    outline: 'none',
    boxShadow: 'none',
    color: v2blue400,
  },
  '& .disabledBtn, & .disabledBtn:focus, & .disabledBtn:active, & .disabledBtn:hover': {
    border: 'none',
    pointerEvents: 'none',
    textDecoration: 'none',
    outline: 'none',
    boxShadow: 'none',
    backgroundColor: blue600,
  },
  '& .disabledBtn::after': {
    border: 'none',
    pointerEvents: 'none',
    textDecoration: 'none',
    outline: 'none',
    boxShadow: 'none',
  },
}

const ContentBlock = glamorous.div({
  padding: '0 10%',
  '@media (max-width: 414px)': {
    padding: '0 3%',
  },
  '& hr': {
    margin: '24px 0',
  },
  '& .aboutBlock': {
    color: v2black,
    fontWeight: 'bold',
    fontSize: '0.857rem',
    lineHeight: '1rem',
  },
  '& .aboutBlock + p': {
    color: v2grey600,
    fontSize: '0.857rem',
    lineHeight: '1rem',
  },
  '& .contacts': {
    '& p, & a': {
      fontSize: '0.857rem',
      lineHeight: '1rem',
    },
    '& .email span': {
      color: grey700,
    },
    '& p': {
      '& span': {
        marginRight: '0.375rem',
        transform: 'translateY(4px)',
      },
    },
    '& a svg': {
      transform: 'translateY(2px)',
    },
  },
  '& .payButton': {
    textAlign: 'center',
    '& button': {
      padding: 0,
      margin: '40px auto',
      lineHeight: 1,
      width: 226,
      height: 36,
    },
  },
  ...disabledMod,
})

const ConfirmationBlock = glamorous.div({
  textAlign: 'center',
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  '& p': {
    margin: '70px 0 36px 0',
    '@media (max-width: 414px)': {
      margin: '40px 0 20px 0',
    },
    color: grey600,
    fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
    fontSize: '1rem',
  },
})

const ShareIcon = glamorous.div({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: 15,
  color: blue600,
  backgroundColor: v2neutral100,
  width: 27,
  height: 27,
  borderRadius: '50%',
})

const SellerName = glamorous.h2({
  fontSize: '1.125rem',
  fontWeight: 'normal',
  lineHeight: '1.5rem',
  margin: '12% 0 5px 0',
  padding: '10px 0 0 0',
})

const Slug = glamorous.p({
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  fontSize: '0.857rem',
  lineHeight: '1rem',
})

const LocationText = glamorous.p({
  color: grey600,
  fontFamily: 'PayPalSansBig-Regular,"Helvetica Neue",Arial,sans-serif',
  fontSize: '0.857rem',
  lineHeight: '1rem',
  marginBottom: 20,
})

const LocationTextPlaceHolder = glamorous.p({
  clear: 'both',
  width: '100%',
  height: 5,
})

function Preview(props) {
  const {
    sellerProfileState: {
      state: {
        businessName,
        businessHandle,
        businessDescription,
        businessPhone,
        businessEmail,
        businessLocation,
        socialMedia: {
          instagramHandle,
          facebookUrl,
          twitterHandle,
          websiteUrl,
        } = {},
        photoSettings: {
          initialCoverPhoto = undefined,
          coverPhoto = '',
          profilePhoto = '',
        } = {},
      } = {},
    } = {},
  } = props

  const [hasProfilePhoto, setHasProfilePhoto] = useState(false)

  const createSellerProfile = () => {
    props.sellerProfileState.createSellerProfile(onSuccessCreate, onErrorCreate)
  }

  const onSuccessCreate = (profile) => {
    const {
      createSeller: {
        handle: businessHandle = '',
        userInfo: { alternateFullName: businessName = '' } = {},
      } = {},
    } = profile
    props.history.replace({
      pathname: '/seller/success',
      state: {
        businessHandle,
        businessName,
      },
    })
  }

  const onErrorCreate = () => {
    // TODO: what if there's ANOTHER error ???
    props.history.push('/seller/error')
  }

  useEffect(() => {
    setHasProfilePhoto(profilePhoto !== '')
  })

  return (
    <Container data-test-id="seller-preview-screen">
      <HeadingText size="md" role="heading" aria-level="1">
        {l10n('title')}
      </HeadingText>
      <p>{l10n('description')}</p>
      <ShadowBlock>
        <BackgroundPhoto image={coverPhoto || initialCoverPhoto}>
          {/* inner div here for maintaining the aspect ratio*/}
          <div role="banner">
            <Logo image={profilePhoto}>
              {/* show generic logo when it's not provided */}
              {hasProfilePhoto ? null : (
                <GenericAvatar
                  background={blue100}
                  data-test-id="profile-card-photo-generic"
                >
                  <V2MerchantIcon size="xl2" />
                </GenericAvatar>
              )}
            </Logo>
          </div>
        </BackgroundPhoto>
        <ContentBlock>
          <SellerName role="heading" aria-level="2">
            {businessName}
          </SellerName>
          <Slug>@{businessHandle}</Slug>
          {businessLocation && businessLocation !== 'donotshow' ? (
            <LocationText>{businessLocation}</LocationText>
          ) : (
            <LocationTextPlaceHolder />
          )}

          {instagramHandle ? (
            <ShareIcon>
              <V2ShareInstagramIcon size="xs" />
            </ShareIcon>
          ) : null}

          {facebookUrl ? (
            <ShareIcon>
              <V2ShareFacebookIcon size="xs" />
            </ShareIcon>
          ) : null}

          {twitterHandle ? (
            <ShareIcon>
              <V2ShareTwitterIcon size="xs" />
            </ShareIcon>
          ) : null}

          {websiteUrl ? (
            <ShareIcon>
              <V2WebsiteIcon size="xs" />
            </ShareIcon>
          ) : null}

          <ShareIcon>
            <V2ShareIcon size="xs" />
          </ShareIcon>

          <Divider />
          <HeadingText className="aboutBlock" size="sm">
            {l10n('about')}
          </HeadingText>
          <p>{businessDescription}</p>
          <div className="contacts">
            {businessPhone && businessPhone !== 'donotshow' ? (
              <p>
                <V2PhoneIcon size="xs" />
                {businessPhone}
              </p>
            ) : null}
            {businessEmail && businessEmail !== 'donotshow' ? (
              <Link
                className="email disabledLink"
                href={`mailto:${businessEmail}`}
                leftIcon={<V2MailIcon size="xs" />}
                theme="v2"
              >
                {businessEmail}
              </Link>
            ) : null}
          </div>
          <div className="payButton">
            <Button disabled className="disabledBtn">
              {l10n('pay')}
            </Button>
          </div>
        </ContentBlock>
      </ShadowBlock>
      <ConfirmationBlock>
        <p>{l10n('lookConfirm')}</p>
        <Button onClick={createSellerProfile}>{l10n('finish')}</Button>
      </ConfirmationBlock>
    </Container>
  )
}
export default Preview
